import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export function QuestionnaireShell({ title, step, total, children, onBack, backTo = "/" }) {
    const pct = Math.round((step / total) * 100);
    return (
        <div className="min-h-screen bg-ivory" data-testid="questionnaire-shell">
            <header className="sticky top-0 z-30 border-b border-charcoal/5 bg-ivory/85 backdrop-blur">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4 md:px-8">
                    {onBack ? (
                        <button
                            type="button"
                            onClick={onBack}
                            data-testid="q-back"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/70 hover:text-charcoal"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back
                        </button>
                    ) : (
                        <Link
                            to={backTo}
                            data-testid="q-back-home"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/70 hover:text-charcoal"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Exit
                        </Link>
                    )}
                    <div className="text-xs tracking-wider text-ink/70" data-testid="q-step">
                        Step {step} of {total}
                    </div>
                </div>
                <div className="mx-auto max-w-3xl px-6 md:px-8">
                    <div className="h-1 w-full overflow-hidden rounded-full bg-champagne">
                        <div
                            className="h-full rounded-full bg-gold transition-all duration-500"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-6 py-10 md:px-8 md:py-14">
                {title && (
                    <h1 className="font-serif text-[34px] leading-tight text-charcoal md:text-[44px] md:leading-[1.08] text-balance">
                        {title}
                    </h1>
                )}
                <div className="mt-8">{children}</div>
            </main>
        </div>
    );
}

export function Field({ label, required, children, testId }) {
    return (
        <div data-testid={testId}>
            <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">
                {label} {required && <span className="text-gold">*</span>}
            </label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

export function TextInput(props) {
    return (
        <input
            {...props}
            className={`w-full rounded-full border border-charcoal/10 bg-white px-5 py-3.5 text-sm text-charcoal focus:border-gold focus:outline-none ${props.className || ""}`}
        />
    );
}

export function OptionTile({ selected, onSelect, label, sub, testId }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            data-testid={testId}
            className={`w-full rounded-[20px] border px-5 py-4 text-left transition ${
                selected
                    ? "border-gold bg-champagne/60 shadow-soft"
                    : "border-charcoal/10 bg-white hover:border-charcoal/25"
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[15px] font-medium text-charcoal">{label}</div>
                    {sub && <div className="mt-1 text-xs text-ink">{sub}</div>}
                </div>
                <div
                    className={`mt-0.5 h-4 w-4 flex-none rounded-full border-2 ${
                        selected ? "border-gold bg-gold" : "border-charcoal/20"
                    }`}
                />
            </div>
        </button>
    );
}

export function PrimaryButton({ children, disabled, onClick, type = "button", testId }) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="btn-gold w-full disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
        >
            {children}
        </button>
    );
}

export function GhostButton({ children, onClick, type = "button", testId }) {
    return (
        <button
            type={type}
            onClick={onClick}
            data-testid={testId}
            className="btn-ghost w-full"
        >
            {children}
        </button>
    );
}
